import { FieldPath, FieldValues, UseControllerProps, useController } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { faAngleDown, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getEngageConfiguration } from 'api/engage';
import * as DropdownMenu from 'components/dropdown-menu';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { EngageTag, MergeField } from 'types/domain';

interface MergeFieldDropdownInputProps<T extends FieldValues, K extends FieldPath<T>>
    extends UseControllerProps<T, K> {
    disabled?: boolean;
    onChange?: (item: { name?: string; customFieldId?: number }) => void;
    item?: Omit<MergeField, 'id'>;
}

const MergeFieldDropdownInput = <T extends FieldValues, K extends FieldPath<T>>({
    item,
    disabled,
    defaultValue,
    control,
    name,
    rules,
    onChange: onChangeExternal,
}: MergeFieldDropdownInputProps<T, K>) => {
    const {
        field: { value, onChange },
        fieldState: { invalid },
    } = useController({
        name,
        control,
        rules,
        defaultValue,
    });

    const { accountId } = useCurrentAccount();

    const { data: options } = useQuery({
        queryKey: accountQueryKeys.engageConfiguration(accountId),
        queryFn: () => getEngageConfiguration(accountId),
        select: data => {
            return data.tags.items;
        },
    });

    const getLabel = (name?: string) =>
        options
            ?.reduce((all, { options }) => [...all, ...(options || [])], [] as EngageTag[])
            .find(option => option?.value === name)?.title || item?.title;

    const onSelectItem = ({ value, id }: EngageTag) => {
        const item = { name: value, customFieldId: id };
        onChangeExternal?.(item);
        onChange(item);
    };

    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger
                disabled={disabled}
                className={`group flex w-full items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm focus-within:ring-blue-500 focus:ring-2 focus:ring-blue-500 ${
                    disabled ? 'cursor-not-allowed bg-gray-50 opacity-80' : ''
                } ${invalid ? 'ring-2 ring-red-500' : ''}`}
            >
                {value.name ? getLabel(value.name) : 'Select field'}
                <FontAwesomeIcon className="group-radix-state-open:rotate-180" icon={faAngleDown} />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="start">
                {options?.map(item => (
                    <DropdownMenu.Sub key={item.title}>
                        <DropdownMenu.SubTrigger className="flex cursor-pointer items-center justify-between rounded-md p-2 focus:bg-neutral-100">
                            {item.title}
                            <FontAwesomeIcon icon={faAngleRight} />
                        </DropdownMenu.SubTrigger>
                        <DropdownMenu.SubContent>
                            {item.options?.map(item => (
                                <DropdownMenu.Item
                                    key={item.value}
                                    onSelect={() => item.value && onSelectItem(item)}
                                >
                                    {item.title}
                                </DropdownMenu.Item>
                            ))}
                        </DropdownMenu.SubContent>
                    </DropdownMenu.Sub>
                ))}
            </DropdownMenu.Content>
        </DropdownMenu.Root>
    );
};

export { MergeFieldDropdownInput };
